// SPDX-License-Identifier: LicenseRef-Proprietary

export type NodeType = 'decision' | 'chance' | 'terminal';

export interface TreeNode {
  id: string;
  type: NodeType;
  label: string;
  nodeType?: 'healthy' | 'infection' | 'hospitalization' | 'death' | 'recover' | 'survive' | 'other'; // Epidemiological outcome type
  cost?: number;   // € (Terminal nodes)
  qaly?: number;   // 0-1 (Terminal nodes)
  notes?: string;
  children: string[]; // child node IDs
}

export interface TreeEdge {
  parentId: string;
  childId: string;
  probability?: number; // 0-1 (for Chance nodes only)
  label?: string;       // optional branch label
  transition_cost?: number; // ≥ 0, cost directly linked to the event (optional)
  transition_utility_loss?: number; // 0-1, utility loss caused by the event (optional)
}

export interface ValidationIssue {
  id: string;          // unique issue ID
  nodeId?: string;     // affected node (if applicable)
  severity: 'error' | 'warning';
  type: 'probability_sum' | 'missing_children' | 'invalid_value' | 'cycle' | 'missing_terminal' | 'orphan' | 'invalid_range';
  message: string;     // human-readable
}

export interface ValidationRules {
  probabilityTolerance: number;        // ±0.1%
  probabilitySumTarget: number;
  maxTreeDepth: number;
  maxChildrenPerNode: number;
  minDecisionChildren: number;
  minChanceChildren: number;
  maxTerminalCostEur: number;
  qalyRange: [number, number];
}

// EasyHTA parameter interfaces for validation
export interface ModelMetadata {
  model_name: string;
  currency: string;
  perspective: 'healthcare' | 'societal';
}

export interface CostParameters {
  cost_vaccine_dose: number;
  cost_admin: number;
  cost_infection: number;
  cost_hospitalization?: number;
  cost_productivity_per_case?: number;
  cost_adverse_event: number;
  efficacy: number;
  adverse_event_qaly_loss: number;
  adverse_event_rate: number;
  doses_per_course: number;
  discount_vaccine_cost?: number;
  booster_cost?: number; // Multi-year: booster cost (≥ 0) - backend API uses snake_case
}

export interface PopulationParameters {
  population_size: number;
  coverage_rate: number;
  age_group: string;
  baseline_incidence: number;
  baseline_mortality?: number; // Background all-cause mortality (p_death_bg). Omit when mortality_source is provided; backend treats omission as 0.
  mortality_source?: 'finnish_lifetable_2023'; // Backend-resolved background mortality source; when set, backend resolves per-cycle p_death_bg and ignores baseline_mortality.
  starting_age?: number; // Cohort starting age (0-100). Required when mortality_source is provided.
  case_fatality_rate?: number; // Infection-related mortality (excess mortality among infected individuals) - required for multi-year analysis
  hospitalization_rate?: number;
  background_mortality?: number;
  utility_healthy: number;
  utility_infection: number;
  duration_infection: number;
  utility_hospitalization?: number;
  duration_hospitalization?: number;
  waning_rate?: number;
  booster_years?: number[]; // Multi-year: booster schedule (integers in [2..cycles]) - backend API uses snake_case
  // Optional per-cycle schedules for years 2..H. When present, backend bypasses
  // the corresponding scalar/decay logic and uses the schedule verbatim.
  ve_schedule?: number[];
  incidence_schedule?: number[];
  qaly_loss_schedule?: number[];
  background_utility_source?: string;
  regional_parameters?: {
    id: string;
    name: string;
    population: number;
    coverage: number;
  }[];
}

export const DEFAULT_VALIDATION_RULES: ValidationRules = {
  probabilityTolerance: 0.001,        // ±0.1%
  probabilitySumTarget: 1.0,
  maxTreeDepth: 20,
  maxChildrenPerNode: 10,
  minDecisionChildren: 2,
  minChanceChildren: 2,
  maxTerminalCostEur: 1_000_000,
  qalyRange: [0, 1],
};

export interface TreeState {
  nodes: Record<string, TreeNode>;
  edges: TreeEdge[];
  rootId: string | null;
}

/**
 * Validates a decision tree state and returns all issues found
 */
export function validateTree(
  state: TreeState, 
  rules: ValidationRules = DEFAULT_VALIDATION_RULES
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  
  // Check for root node
  if (!state.rootId) {
    issues.push({
      id: 'no-root',
      severity: 'error',
      type: 'missing_children',
      message: 'Tree must have a root node'
    });
    return issues; // Can't validate further without root
  }

  // Check if root exists in nodes
  if (!state.nodes[state.rootId]) {
    issues.push({
      id: 'invalid-root',
      nodeId: state.rootId,
      severity: 'error',
      type: 'missing_children',
      message: 'Root node does not exist in nodes collection'
    });
    return issues;
  }

  // Validate each node
  Object.values(state.nodes).forEach(node => {
    issues.push(...validateNode(node, state, rules));
  });

  // Check for cycles
  issues.push(...detectCycles(state));

  // Check for orphaned nodes
  issues.push(...detectOrphans(state));

  // Check tree depth
  issues.push(...checkTreeDepth(state, rules));

  // Validate edges (transition costs and utility losses)
  issues.push(...validateEdges(state, rules));

  return issues;
}

/**
 * Validates a single node
 */
function validateNode(
  node: TreeNode, 
  state: TreeState, 
  rules: ValidationRules
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // Check required fields
  if (!node.label.trim()) {
    issues.push({
      id: `empty-label-${node.id}`,
      nodeId: node.id,
      severity: 'error',
      type: 'invalid_value',
      message: 'Node label cannot be empty'
    });
  }

  // Check children count limits
  if (node.children.length > rules.maxChildrenPerNode) {
    issues.push({
      id: `too-many-children-${node.id}`,
      nodeId: node.id,
      severity: 'warning',
      type: 'invalid_value',
      message: `Node has ${node.children.length} children (max: ${rules.maxChildrenPerNode})`
    });
  }

  // Type-specific validation
  switch (node.type) {
    case 'decision':
      if (node.children.length < rules.minDecisionChildren) {
        issues.push({
          id: `insufficient-decision-children-${node.id}`,
          nodeId: node.id,
          severity: 'error',
          type: 'missing_children',
          message: `Decision nodes must have at least ${rules.minDecisionChildren} children`
        });
      }
      break;

    case 'chance':
      if (node.children.length < rules.minChanceChildren) {
        issues.push({
          id: `insufficient-chance-children-${node.id}`,
          nodeId: node.id,
          severity: 'error',
          type: 'missing_children',
          message: `Chance nodes must have at least ${rules.minChanceChildren} children`
        });
      }
      
      // Check probability sum
      const probabilities = state.edges
        .filter(edge => edge.parentId === node.id)
        .map(edge => edge.probability || 0);
      
      if (probabilities.length > 0) {
        const sum = probabilities.reduce((acc, p) => acc + p, 0);
        const target = rules.probabilitySumTarget;
        const tolerance = rules.probabilityTolerance;
        
        if (Math.abs(sum - target) > tolerance) {
          issues.push({
            id: `probability-sum-${node.id}`,
            nodeId: node.id,
            severity: 'error',
            type: 'probability_sum',
            message: `Probabilities sum to ${sum.toFixed(3)} (must be ${target} ± ${tolerance})`
          });
        }
      }
      break;

    case 'terminal':
      // Terminal nodes should not have children
      if (node.children.length > 0) {
        issues.push({
          id: `terminal-with-children-${node.id}`,
          nodeId: node.id,
          severity: 'warning',
          type: 'invalid_value',
          message: 'Terminal nodes should not have children'
        });
      }

      // Validate cost range
      if (node.cost !== undefined) {
        if (node.cost < 0 || node.cost > rules.maxTerminalCostEur) {
          issues.push({
            id: `invalid-cost-${node.id}`,
            nodeId: node.id,
            severity: 'error',
            type: 'invalid_range',
            message: `Cost must be between 0 and ${rules.maxTerminalCostEur.toLocaleString()}€`
          });
        }
      }

      // Validate QALY range
      if (node.qaly !== undefined) {
        const [minQaly, maxQaly] = rules.qalyRange;
        if (node.qaly < minQaly || node.qaly > maxQaly) {
          issues.push({
            id: `invalid-qaly-${node.id}`,
            nodeId: node.id,
            severity: 'error',
            type: 'invalid_range',
            message: `QALY must be between ${minQaly} and ${maxQaly}`
          });
        }
      }
      break;
  }

  return issues;
}

/**
 * Detects cycles in the tree using DFS
 */
function detectCycles(state: TreeState): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  function dfs(nodeId: string, path: string[]): void {
    if (recursionStack.has(nodeId)) {
      // Found a cycle
      const cycleStart = path.indexOf(nodeId);
      const cycle = path.slice(cycleStart).concat([nodeId]);
      issues.push({
        id: `cycle-${nodeId}`,
        nodeId: nodeId,
        severity: 'error',
        type: 'cycle',
        message: `Cycle detected: ${cycle.join(' → ')}`
      });
      return;
    }

    if (visited.has(nodeId)) {
      return;
    }

    visited.add(nodeId);
    recursionStack.add(nodeId);

    const node = state.nodes[nodeId];
    if (node) {
      node.children.forEach(childId => {
        dfs(childId, [...path, nodeId]);
      });
    }

    recursionStack.delete(nodeId);
  }

  // Start DFS from root
  if (state.rootId) {
    dfs(state.rootId, []);
  }

  return issues;
}

/**
 * Detects orphaned nodes (nodes not reachable from root)
 */
function detectOrphans(state: TreeState): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const reachable = new Set<string>();

  function markReachable(nodeId: string): void {
    if (reachable.has(nodeId)) return;
    
    reachable.add(nodeId);
    const node = state.nodes[nodeId];
    if (node) {
      node.children.forEach(childId => markReachable(childId));
    }
  }

  // Mark all nodes reachable from root
  if (state.rootId) {
    markReachable(state.rootId);
  }

  // Find orphaned nodes
  Object.keys(state.nodes).forEach(nodeId => {
    if (!reachable.has(nodeId)) {
      issues.push({
        id: `orphan-${nodeId}`,
        nodeId: nodeId,
        severity: 'warning',
        type: 'orphan',
        message: 'Node is not reachable from root'
      });
    }
  });

  return issues;
}

/**
 * Checks if tree depth exceeds maximum
 */
function checkTreeDepth(state: TreeState, rules: ValidationRules): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  function getDepth(nodeId: string, visited: Set<string> = new Set()): number {
    if (visited.has(nodeId)) return 0; // Avoid infinite recursion in cycles
    
    visited.add(nodeId);
    const node = state.nodes[nodeId];
    if (!node || node.children.length === 0) {
      return 1;
    }

    const maxChildDepth = Math.max(
      ...node.children.map(childId => getDepth(childId, new Set(visited)))
    );
    
    return 1 + maxChildDepth;
  }

  if (state.rootId) {
    const depth = getDepth(state.rootId);
    if (depth > rules.maxTreeDepth) {
      issues.push({
        id: 'tree-too-deep',
        severity: 'warning',
        type: 'invalid_value',
        message: `Tree depth is ${depth} (max: ${rules.maxTreeDepth})`
      });
    }
  }

  return issues;
}

/**
 * Validates edges for transition costs and utility losses
 */
function validateEdges(state: TreeState, rules: ValidationRules): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  state.edges.forEach(edge => {
    // Validate transition_cost
    if (edge.transition_cost !== undefined) {
      if (edge.transition_cost < 0) {
        issues.push({
          id: `invalid-transition-cost-${edge.parentId}-${edge.childId}`,
          nodeId: edge.parentId,
          severity: 'error',
          type: 'invalid_range',
          message: `Transition cost must be ≥ 0 (found: ${edge.transition_cost})`
        });
      }
    }

    // Validate transition_utility_loss
    if (edge.transition_utility_loss !== undefined) {
      const [minQaly, maxQaly] = rules.qalyRange;
      if (edge.transition_utility_loss < minQaly || edge.transition_utility_loss > maxQaly) {
        issues.push({
          id: `invalid-transition-utility-${edge.parentId}-${edge.childId}`,
          nodeId: edge.parentId,
          severity: 'error',
          type: 'invalid_range',
          message: `Transition utility loss must be between ${minQaly} and ${maxQaly} (found: ${edge.transition_utility_loss})`
        });
      }
    }
  });

  return issues;
}

/**
 * Validates EasyHTA parameters
 */
export interface SimulationParameters {
  cycles: number;
  willingness_to_pay: number;
  discount_rate: number;
  discounting_enabled?: boolean; // Multi-year: only enabled when cycles > 1
}

export const DEFAULT_SIMULATION_PARAMETERS: SimulationParameters = {
  cycles: 1,
  willingness_to_pay: 20000,
  discount_rate: 0.03
};

/**
 * Validates EasyHTA parameters
 */
export function validateEasyHTAParameters(
  modelMetadata: ModelMetadata,
  costParameters: CostParameters,
  populationParameters: PopulationParameters,
  simulationParameters: SimulationParameters = DEFAULT_SIMULATION_PARAMETERS
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // Validate model metadata
  if (!modelMetadata.model_name || modelMetadata.model_name.trim() === '') {
    issues.push({
      id: 'invalid-model-name',
      severity: 'error',
      type: 'invalid_value',
      message: 'Model name cannot be empty'
    });
  }

  // Validate cost parameters
  if (costParameters.cost_vaccine_dose < 0) {
    issues.push({
      id: 'invalid-cost-vaccine-dose',
      severity: 'error',
      type: 'invalid_range',
      message: 'Vaccine dose cost must be ≥ 0'
    });
  }
  if (costParameters.cost_admin < 0) {
    issues.push({
      id: 'invalid-cost-admin',
      severity: 'error',
      type: 'invalid_range',
      message: 'Administration cost must be ≥ 0'
    });
  }
  if (costParameters.cost_infection < 0) {
    issues.push({
      id: 'invalid-cost-infection',
      severity: 'error',
      type: 'invalid_range',
      message: 'Infection cost must be ≥ 0'
    });
  }
  if (costParameters.cost_productivity_per_case !== undefined && costParameters.cost_productivity_per_case < 0) {
    issues.push({
      id: 'invalid-cost-productivity',
      severity: 'error',
      type: 'invalid_range',
      message: 'Productivity cost per case must be ≥ 0'
    });
  }
  if (costParameters.cost_adverse_event !== undefined && costParameters.cost_adverse_event < 0) {
    issues.push({
      id: 'invalid-cost-adverse-event',
      severity: 'error',
      type: 'invalid_range',
      message: 'Adverse event cost must be ≥ 0'
    });
  }
  if (costParameters.discount_vaccine_cost !== undefined && (costParameters.discount_vaccine_cost < 0 || costParameters.discount_vaccine_cost > 1)) {
    issues.push({
      id: 'invalid-discount-vaccine-cost',
      severity: 'error',
      type: 'invalid_range',
      message: 'Vaccine cost discount must be between 0 and 1'
    });
  }

  // Validate population parameters
  if (populationParameters.population_size <= 0) {
    issues.push({
      id: 'invalid-population-size',
      severity: 'error',
      type: 'invalid_range',
      message: 'Population size must be > 0'
    });
  }
  if (populationParameters.coverage_rate < 0 || populationParameters.coverage_rate > 1) {
    issues.push({
      id: 'invalid-coverage-rate',
      severity: 'error',
      type: 'invalid_range',
      message: 'Coverage rate must be between 0 and 1'
    });
  }
  if (populationParameters.baseline_incidence < 0 || populationParameters.baseline_incidence > 1) {
    issues.push({
      id: 'invalid-baseline-incidence',
      severity: 'error',
      type: 'invalid_range',
      message: 'Baseline incidence must be between 0 and 1'
    });
  }
  if ((populationParameters.baseline_mortality ?? 0) < 0 || (populationParameters.baseline_mortality ?? 0) > 1) {
    issues.push({
      id: 'invalid-baseline-mortality',
      severity: 'error',
      type: 'invalid_range',
      message: 'Baseline mortality must be between 0 and 1'
    });
  }
  if (populationParameters.background_mortality !== undefined && (populationParameters.background_mortality < 0 || populationParameters.background_mortality > 1)) {
    issues.push({
      id: 'invalid-background-mortality',
      severity: 'error',
      type: 'invalid_range',
      message: 'Background mortality must be between 0 and 1'
    });
  }
  if (populationParameters.utility_healthy < 0 || populationParameters.utility_healthy > 1) {
    issues.push({
      id: 'invalid-utility-healthy',
      severity: 'error',
      type: 'invalid_range',
      message: 'Utility healthy must be between 0 and 1'
    });
  }
  if (populationParameters.utility_infection < 0 || populationParameters.utility_infection > 1) {
    issues.push({
      id: 'invalid-utility-infection',
      severity: 'error',
      type: 'invalid_range',
      message: 'Utility infection must be between 0 and 1'
    });
  }
  if (populationParameters.utility_hospitalization !== undefined) {
    if (populationParameters.utility_hospitalization < 0 || populationParameters.utility_hospitalization > 1) {
      issues.push({
        id: 'invalid-utility-hospitalization',
        severity: 'error',
        type: 'invalid_range',
        message: 'Utility hospitalization must be between 0 and 1'
      });
    }
    // Validate that utility during infection is not lower than utility during hospitalization
    // Hospitalization should have lower (or equal) utility than non-hospitalized infection
    if (populationParameters.utility_infection < populationParameters.utility_hospitalization) {
      issues.push({
        id: 'invalid-utility-infection-lower-than-hospitalization',
        severity: 'error',
        type: 'invalid_range',
        message: 'Utility during infection cannot be lower than utility during hospitalization. Hospitalization should have lower or equal utility compared to non-hospitalized infection.'
      });
    }
  }
  if (populationParameters.duration_infection < 0) {
    issues.push({
      id: 'invalid-duration-infection',
      severity: 'error',
      type: 'invalid_range',
      message: 'Duration infection must be ≥ 0'
    });
  }
  if (populationParameters.hospitalization_rate !== undefined) {
    if (populationParameters.hospitalization_rate < 0 || populationParameters.hospitalization_rate > 1) {
      issues.push({
        id: 'invalid-hospitalization-rate',
        severity: 'error',
        type: 'invalid_range',
        message: 'Hospitalization rate must be between 0 and 1'
      });
    }
    if ((populationParameters.baseline_mortality ?? 0) + populationParameters.hospitalization_rate > 1) {
      issues.push({
        id: 'invalid-mortality-hospitalization-sum',
        severity: 'error',
        type: 'invalid_range',
        message: 'Sum of mortality and hospitalization rates must be ≤ 1'
      });
    }
  }

  // Validate simulation parameters
  if (simulationParameters.cycles < 1) {
    issues.push({
      id: 'invalid-cycles',
      severity: 'error',
      type: 'invalid_range',
      message: 'Time horizon (cycles) must be ≥ 1'
    });
  }
  if (simulationParameters.willingness_to_pay < 0) {
    issues.push({
      id: 'invalid-wtp',
      severity: 'error',
      type: 'invalid_range',
      message: 'Willingness to pay must be ≥ 0'
    });
  }
  if (simulationParameters.discount_rate < 0 || simulationParameters.discount_rate > 1) {
    issues.push({
      id: 'invalid-discount-rate',
      severity: 'error',
      type: 'invalid_range',
      message: 'Discount rate must be between 0 and 1'
    });
  }

  return issues;
}

/**
 * Gets validation status summary
 */
export function getValidationStatus(issues: ValidationIssue[]): {
  className: string;
  text: string;
  hasIssues: boolean;
} {
  const errors = issues.filter(i => i.severity === 'error');
  const warnings = issues.filter(i => i.severity === 'warning');

  if (errors.length > 0) {
    return {
      className: 'status-badge--error',
      text: `${errors.length} error${errors.length > 1 ? 's' : ''}`,
      hasIssues: true
    };
  }

  if (warnings.length > 0) {
    return {
      className: 'status-badge--warning',
      text: `${warnings.length} warning${warnings.length > 1 ? 's' : ''}`,
      hasIssues: true
    };
  }

  return {
    className: 'status-badge--success',
    text: 'OK',
    hasIssues: false
  };
}
